import cn from 'classnames';
import { FunctionComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { isTouchDevice } from '../../../../utils/device';
import { getRawAsset, isLiveStream } from '../../../../utils/player/asset';
import { usePlayer } from '../../hooks/PlayerProvider';
import { useAdData } from '../../hooks/useAdData';
import { useJWPlayerEvent } from '../../hooks/useJWPlayerEvent';
import { useMidrollCues } from '../../hooks/useMidrollCues';
import { usePrevious } from '../../hooks/usePrevious';
import { useSeek } from '../../hooks/useSeek';
import { useSliderControl } from '../../hooks/useSliderControl';
import { ButtonUnstyled } from '../ButtonUnstyled/ButtonUnstyled';

const getProgressPercent = ({
  duration,
  position,
  isLive,
}: {
  duration: number;
  position: number;
  isLive: boolean;
}) => {
  if (isLive) {
    const diff = duration - position;

    return (diff / duration) * 100;
  }

  return (position / duration) * 100;
};

interface ProgressBarProps {
  onInteraction: (isActive: boolean) => void;
  className?: string;
  readOnly?: boolean;
}

export const ProgressBar: FunctionComponent<ProgressBarProps> = ({
  onInteraction,
  className,
  readOnly,
}) => {
  const player = usePlayer();
  const { seekTo } = useSeek();
  const { midrolls } = useMidrollCues();
  const railRef = useRef<HTMLDivElement>(null!);
  const sliderRef = useRef<HTMLDivElement>(null!);

  const asset = getRawAsset(player);
  const { adData, isAdLoaded } = useAdData(asset);
  const isAdPlaying = !!(adData || isAdLoaded);
  const isLive = asset !== undefined && isLiveStream(asset);

  const [progressPercent, setProgressPercent] = useState(
    getProgressPercent({
      isLive,
      position: player.getCurrentTime(),
      duration: player.getDuration(),
    })
  );
  const [bufferPercent, setBufferPercent] = useState(
    player.getJWPlayer()?.getBuffer()
  );

  const handleBufferChange = useCallback(
    (param: jwplayer.ProviderEvents['bufferChange']) => {
      setBufferPercent(param.bufferPercent);
    },
    []
  );

  const handleSeek = useCallback((param: jwplayer.SeekParam) => {
    setProgressPercent((param.offset / Math.abs(param.duration)) * 100);
  }, []);

  const handleTime = useCallback(
    (param: jwplayer.TimeParam | jwplayer.AdTimeParam) => {
      setProgressPercent(
        getProgressPercent({
          position: param.position,
          duration: param.duration,
          isLive,
        })
      );
    },
    [isLive]
  );

  useJWPlayerEvent('bufferChange', handleBufferChange);
  useJWPlayerEvent('seek', handleSeek);
  useJWPlayerEvent('time', handleTime);
  useJWPlayerEvent('adTime', handleTime);

  const handleSliderValueChange = useCallback(
    (value: number) => {
      if (readOnly) return;
      seekTo(value);
    },
    [seekTo, readOnly]
  );

  const handleInteractionStart = useCallback(() => {
    if (readOnly) return;
    player.setScrubbing?.(true);
  }, [player, readOnly]);

  const handleInteractionEnd = useCallback(() => {
    player.setScrubbing?.(false);
  }, [player]);

  const { isActive } = useSliderControl({
    sliderRef,
    railRef,
    onSliderValueChange: handleSliderValueChange,
    onInteractionStart: handleInteractionStart,
    onInteractionEnd: handleInteractionEnd,
  });

  // Safety net: a teardown mid-drag (e.g. skin unmount) must not leave the
  // scrubbing flag stuck on.
  useEffect(() => () => player.setScrubbing?.(false), [asset, player]);

  const isActivePrev = usePrevious(isActive);

  useEffect(() => {
    if (isActivePrev === undefined) {
      return;
    }

    onInteraction(isActive);
  }, [isActivePrev, onInteraction, isActive]);

  return (
    <ButtonUnstyled
      component="div"
      className={cn('vgp-progress-bar', className, {
        'vgp-drag': isActive,
        'vgp-progress-bar-read-only': readOnly,
      })}
      ref={sliderRef}
      role="slider"
      tabIndex={readOnly ? -1 : 0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progressPercent}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="vgp-progress-track">
        <div className="vgp-progress-rail" ref={railRef} />
        <div
          className="vgp-buffer-progress"
          style={{ right: `${100 - bufferPercent}%` }}
        />
        <div
          className="vgp-play-progress"
          style={{ right: `${100 - progressPercent}%` }}
        />
        {!isAdPlaying &&
          midrolls.map((midroll, index) => (
            <div
              className="vgp-cue"
              style={{
                left: `calc(${midroll.percentage}% - var(--cue-width) / 2)`,
              }}
              title={midroll.text}
              key={index}
            />
          ))}
      </div>
      {!isAdPlaying && !readOnly && (
        <div
          className={cn('vgp-progress-knob', {
            'vgp-progress-knob-active': isActive || isTouchDevice,
          })}
          style={{
            right: `calc(${
              100 - progressPercent
            }% - var(--progress-knob-size) / 2)`,
          }}
        />
      )}
    </ButtonUnstyled>
  );
};
